import React from 'react';
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { DATA_LAYERS } from '../../../lib/dataLayers';
import { useWildfireHazardPotential } from '../../../hooks/useWildfireHazardPotential';
import { WHP_PALETTE } from '../../../lib/whpColors';
import {
  CHART_TOOLTIP_STYLE,
  CHART_TOOLTIP_LABEL_STYLE,
  CHART_TOOLTIP_ITEM_STYLE,
  CHART_AXIS_STYLE,
  CHART_GRID_STROKE,
  CHART_AXIS_STROKE,
} from '../../../lib/chartStyles';
import { LayerHeader } from './LayerHeader';
import { AboutLayer } from './AboutLayer';
import { GlossaryTerm } from '../../shared/GlossaryTerm';
import {
  LayerLoadingLabel,
  LayerErrorAlert,
  LayerLiveBadge,
  SkeletonPulseBlock,
} from './contentPrimitives';

interface WildfireHazardContentProps {
  millCode: string | null;
  driveTimeMinutes: number;
}

const Skeleton: React.FC = () => (
  <div className="space-y-4">
    <LayerLoadingLabel layer={DATA_LAYERS.fire_risk} />
    <SkeletonPulseBlock className="h-[240px]" />
  </div>
);

const fmtAcres = (n: number) =>
  n >= 1000 ? `${Math.round(n).toLocaleString()} ac` : `${n.toFixed(1)} ac`;
const fmtPct = (f: number) => `${(f * 100).toFixed(1)}%`;

export const WildfireHazardContent: React.FC<WildfireHazardContentProps> = ({
  millCode,
  driveTimeMinutes,
}) => {
  const layer = DATA_LAYERS.fire_risk;
  const { data, isLoading, error } = useWildfireHazardPotential(millCode, driveTimeMinutes);

  const chartData = (data?.classes ?? []).map(c => ({
    class: c.class,
    label: WHP_PALETTE.labels[c.class],
    percent: c.fraction * 100,
    acres: c.burnableAcres,
  }));

  return (
    <div className="space-y-6">
      <LayerHeader layer={layer} />

      <AboutLayer>
        <p>
          <GlossaryTerm term="whp">Wildfire Hazard Potential (WHP)</GlossaryTerm> is a
          5-class rating combining fuels, terrain, and climate drivers into a single
          measure of crown-fire potential. It answers &ldquo;how easily could a fire
          ignite and spread here?&rdquo; rather than tracking any specific past event.
        </p>
        <p>
          Values come from the USFS Wildfire Risk to Communities dataset
          (Scott et al. 2024). Fire behavior was simulated at 30&thinsp;m using FlamMap
          across the full range of seasonal weather conditions, then weighted by weather
          likelihood. Inputs came from LANDFIRE 2020 fuels and vegetation, updated
          through 2022 disturbances. The five classes reflect where each pixel falls in
          the <em>national (CONUS) distribution</em> of WHP values, so your shed&rsquo;s
          mix is directly comparable to forested land across the country.
        </p>
        <p>
          WHP is a long-term relative index &mdash; not a weather-adjusted forecast for
          any given season, and not a substitute for locally-calibrated risk assessments.
          Bars below show the share of <em>burnable</em> land in each hazard class across
          the supply shed; non-burnable area (water, developed, bare) is reported
          separately as a fraction of the full shed.
        </p>
      </AboutLayer>

      {isLoading && <Skeleton />}

      {!isLoading && error && (
        <LayerErrorAlert layer={layer} details={error} />
      )}

      {!isLoading && data && data.totalAcres > 0 && (
        <>
          <LayerLiveBadge />

          <div className="bg-figma-card rounded-figma-panel p-4 border border-figma-lime/30 shadow-figma-card">
            <h4 className="text-base font-roboto font-bold text-figma-text mb-4">
              Share of Burnable Area by Hazard Class <span className="text-neutral-500 font-normal">(%)</span>
            </h4>
            <ResponsiveContainer width="100%" height={240} minWidth={0}>
              <BarChart
                data={chartData}
                layout="vertical"
                margin={{ top: 5, right: 16, left: 8, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID_STROKE} horizontal={false} />
                <XAxis
                  type="number"
                  domain={[0, 100]}
                  stroke={CHART_AXIS_STROKE}
                  style={CHART_AXIS_STYLE}
                  tickFormatter={(v: number) => `${v}%`}
                />
                <YAxis
                  type="category"
                  dataKey="label"
                  stroke={CHART_AXIS_STROKE}
                  style={CHART_AXIS_STYLE}
                  width={80}
                />
                <Tooltip
                  cursor={false}
                  contentStyle={CHART_TOOLTIP_STYLE}
                  labelStyle={CHART_TOOLTIP_LABEL_STYLE}
                  itemStyle={CHART_TOOLTIP_ITEM_STYLE}
                  formatter={(value: number | undefined, _name: string | undefined, props: { payload?: { acres?: number } }) => {
                    const pct = value ?? 0;
                    const acres = props.payload?.acres ?? 0;
                    return [`${pct.toFixed(1)}%  (${fmtAcres(acres)})`, 'Share of burnable'];
                  }}
                />
                <Bar dataKey="percent" isAnimationActive={false}>
                  {chartData.map(d => (
                    <Cell key={d.class} fill={WHP_PALETTE.colors[d.class]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <div className="flex items-center justify-between pt-3 mt-2 border-t border-figma-lime/20 text-sm font-roboto">
              <span className="text-neutral-400">Non-burnable area</span>
              <span className="font-roboto-mono text-neutral-300">
                {fmtPct(data.nonBurnableFraction)}
                <span className="text-neutral-500 ml-2">of shed</span>
              </span>
            </div>
            <p className="text-xs font-roboto italic text-neutral-500 mt-2 leading-relaxed">
              Bars and map colors share the same 5-class national classification &mdash;
              a red pixel on the map is in the same national very-high bucket that the
              Very High bar below counts. High and very high classes are conditions
              where a crown fire is most likely to ignite and spread.
            </p>
          </div>
        </>
      )}
    </div>
  );
};
